import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { PageHeader, StatCard, Card } from '../../components/ui/Card'
import { EmptyState } from '../../components/ui/EmptyState'
import { Badge } from '../../components/ui/Badge'
import { TaskListView } from '../tasks/shared/TaskListView'
import { useAuth } from '../../auth/AuthContext'
import { usersApi } from '../../api/users.api'
import { rolesApi } from '../../api/roles.api'
import { projectsApi } from '../../api/projects.api'
import { tasksApi } from '../../api/tasks.api'
import { remindersApi } from '../../api/reminders.api'
import styles from '../../components/ui/Card.module.css'

export function DashboardPage() {
  const { me, hasPermission } = useAuth()
  const navigate = useNavigate()

  const usersQuery = useQuery({
    queryKey: ['dashboard', 'users-count'],
    queryFn: async () => (await usersApi.list({ pageSize: 1 })).meta.total,
    enabled: hasPermission('users.view'),
  })

  const rolesQuery = useQuery({
    queryKey: ['dashboard', 'roles-count'],
    queryFn: async () => (await rolesApi.list()).data.length,
    enabled: hasPermission('users.view'),
  })

  const activeProjectsQuery = useQuery({
    queryKey: ['dashboard', 'active-projects'],
    queryFn: async () => (await projectsApi.list({ status: 'ACTIVE', pageSize: 1 })).meta.total,
    enabled: hasPermission('projects.view'),
  })

  const myTasksQuery = useQuery({
    queryKey: ['dashboard', 'my-tasks'],
    queryFn: async () => (await tasksApi.list({ mine: true, pageSize: 200 })).data,
    enabled: hasPermission('tasks.view'),
  })

  const remindersQuery = useQuery({ queryKey: ['reminders'], queryFn: async () => (await remindersApi.list()).data })

  const myOpenTasks = myTasksQuery.data?.filter((t) => !t.status.isDone) ?? []
  const myOverdueTasks = myOpenTasks.filter((t) => t.dueDate && new Date(t.dueDate).getTime() < Date.now())
  const upcomingReminders = (remindersQuery.data ?? []).slice(0, 5)

  return (
    <div>
      <PageHeader
        title={`Willkommen, ${me?.user.displayName ?? ''}`}
        subtitle="Überblick über dein Team und den aktuellen Ausbaustand des Panels."
      />

      <div className={styles.grid} style={{ marginBottom: 'var(--space-5)' }}>
        {hasPermission('projects.view') ? <StatCard label="Aktive Projekte" value={activeProjectsQuery.data ?? '—'} /> : null}
        {hasPermission('tasks.view') ? <StatCard label="Meine offenen Aufgaben" value={myOpenTasks.length} /> : null}
        {hasPermission('tasks.view') ? <StatCard label="Überfällige Aufgaben" value={myOverdueTasks.length} /> : null}
        {hasPermission('users.view') ? <StatCard label="Team-Mitglieder" value={usersQuery.data ?? '—'} /> : null}
        {hasPermission('users.view') ? <StatCard label="Rollen" value={rolesQuery.data ?? '—'} /> : null}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 'var(--space-4)', marginBottom: 'var(--space-5)' }}>
        {hasPermission('tasks.view') ? (
          <Card>
            <h3 style={{ marginTop: 0 }}>Meine Aufgaben</h3>
            {myOpenTasks.length > 0 ? (
              <TaskListView tasks={myOpenTasks.slice(0, 8)} onOpenTask={() => navigate('/tasks')} />
            ) : (
              <EmptyState mascot title="Keine offenen Aufgaben" description="Dir sind aktuell keine offenen Aufgaben zugewiesen." />
            )}
          </Card>
        ) : null}

        <Card>
          <h3 style={{ marginTop: 0 }}>Kommende Reminder</h3>
          {upcomingReminders.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {upcomingReminders.map((r) => (
                <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-2)', fontSize: 'var(--text-sm)' }}>
                  <span>{r.message}</span>
                  <Badge variant="neutral">{new Date(r.dueAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</Badge>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState mascot title="Keine anstehenden Reminder" description="Lege im Reminder-Bereich eine Erinnerung an." />
          )}
        </Card>
      </div>
    </div>
  )
}
